import React, { useCallback, useEffect, useState } from 'react';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Button, Card, Col, Form, InputGroup, Modal, Nav, Row, Table } from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';
import Swal from 'sweetalert2';
import { useAuth } from '../services/authProvider';
import { MED_FORMS, MED_SOURCES, formLabel, medSourceLabel, thaiDate, todayISO } from './nurseOptions';

const alertError = (title, err) =>
  Swal.fire({ icon: 'error', title, text: err?.response?.data?.message || '' });

const emptyItem = () => ({
  mi_id: null, code: '', name: '', generic: '', form: 'tablet', unit: 'เม็ด',
  category: '', minStock: 0, isControlled: false, status: 'active', note: '',
});

const emptyLot = () => ({
  lotNo: '', expiryDate: '', qty: '', unitCost: '', source: 'purchase', note: '',
});

// สีของยอดคงเหลือเทียบกับจำนวนขั้นต่ำ — ต้องเห็นได้จากการกวาดตา ไม่ต้องอ่านตัวเลข
const stockVariant = (item) => {
  const stock = Number(item.stock);
  const min = Number(item.mi_min_stock);
  if (stock <= 0) return 'danger';
  if (min > 0 && stock < min) return 'warning';
  return 'success';
};

const expiryVariant = (ymd) => {
  if (!ymd) return 'secondary';
  const days = Math.round((new Date(ymd) - new Date()) / 86400000);
  if (days < 0) return 'danger';
  if (days <= 60) return 'warning';
  return 'secondary';
};

export default function NurseMedicines() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { posIds } = useAuth();
  // รับเข้าคลัง ปรับยอด ทิ้งยา และแก้รายการยา เป็นของพยาบาล (server บังคับอีกชั้น)
  const canManage = (posIds || []).some(p => p === 28 || p === 99);

  const [tab, setTab] = useState('stock');
  const [keyword, setKeyword] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [lots, setLots] = useState([]);
  const [alerts, setAlerts] = useState({ expiring: [], low: [], summary: {} });
  const [txns, setTxns] = useState([]);

  const [itemForm, setItemForm] = useState(emptyItem);
  const [showItemModal, setShowItemModal] = useState(false);
  const [lotForm, setLotForm] = useState(emptyLot);
  const [showLotModal, setShowLotModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(() => {
    const params = new URLSearchParams();
    if (showInactive) params.set('all', '1');
    if (keyword.trim()) params.set('keyword', keyword.trim());
    axiosInstance.get(`/api/nurse/medicines?${params.toString()}`)
      .then(res => setItems(res.data || []))
      .catch(() => setItems([]));
  }, [keyword, showInactive]);

  const fetchAlerts = useCallback(() => {
    axiosInstance.get('/api/nurse/medicines-alerts')
      .then(res => setAlerts(res.data || { expiring: [], low: [], summary: {} }))
      .catch(() => {});
  }, []);

  const fetchTxns = useCallback(() => {
    axiosInstance.get('/api/nurse/medicines-txns?limit=200')
      .then(res => setTxns(res.data || []))
      .catch(() => setTxns([]));
  }, []);

  const fetchLots = useCallback((itemId) => {
    axiosInstance.get(`/api/nurse/medicines/${itemId}/lots`)
      .then(res => setLots(res.data || []))
      .catch(() => setLots([]));
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);
  useEffect(() => { if (tab === 'txns') fetchTxns(); }, [tab, fetchTxns]);
  useEffect(() => { if (selected) fetchLots(selected.mi_id); }, [selected, fetchLots]);

  const refreshAll = () => {
    fetchItems();
    fetchAlerts();
    if (selected) fetchLots(selected.mi_id);
    if (tab === 'txns') fetchTxns();
  };

  const openItem = (item) => {
    setItemForm(item ? {
      mi_id: item.mi_id, code: item.mi_code, name: item.mi_name, generic: item.mi_generic || '',
      form: item.mi_form, unit: item.mi_unit, category: item.mi_category || '',
      minStock: item.mi_min_stock, isControlled: !!item.mi_is_controlled,
      status: item.mi_status, note: item.mi_note || '',
    } : emptyItem());
    setShowItemModal(true);
  };

  const saveItem = (e) => {
    e.preventDefault();
    setSaving(true);
    const req = itemForm.mi_id
      ? axiosInstance.put(`/api/nurse/medicines/${itemForm.mi_id}`, itemForm)
      : axiosInstance.post('/api/nurse/medicines', itemForm);
    req.then(() => {
      setShowItemModal(false);
      refreshAll();
      Swal.fire({ icon: 'success', title: 'บันทึกแล้ว', timer: 1200, showConfirmButton: false });
    }).catch(err => alertError('ไม่สามารถบันทึกรายการยาได้', err))
      .finally(() => setSaving(false));
  };

  const saveLot = (e) => {
    e.preventDefault();
    if (!(Number(lotForm.qty) > 0)) {
      Swal.fire({ icon: 'info', title: 'จำนวนที่รับเข้าต้องมากกว่า 0' });
      return;
    }
    setSaving(true);
    axiosInstance.post(`/api/nurse/medicines/${selected.mi_id}/lots`, lotForm)
      .then(() => {
        setShowLotModal(false);
        setLotForm(emptyLot());
        refreshAll();
        Swal.fire({ icon: 'success', title: 'รับยาเข้าคลังแล้ว', timer: 1300, showConfirmButton: false });
      })
      .catch(err => alertError('ไม่สามารถรับยาเข้าคลังได้', err))
      .finally(() => setSaving(false));
  };

  // ปรับยอด/ทิ้งยา ต้องมีเหตุผลเสมอ — ทั้งคู่ลง tb_audit_log
  const stockAction = async (lot, kind) => {
    const isDispose = kind === 'dispose';
    const { value } = await Swal.fire({
      title: isDispose ? 'ทิ้งยาล็อตนี้' : 'ปรับยอดล็อตนี้',
      html: `<div class="fs-sm">${selected.mi_name}${lot.ml_lot_no ? ` · ล็อต ${lot.ml_lot_no}` : ''}`
        + `<br/>คงเหลือ ${Number(lot.ml_qty_left)} ${selected.mi_unit}</div>`,
      input: 'text',
      inputLabel: isDispose ? `จำนวนที่ทิ้ง (${selected.mi_unit})` : `จำนวนที่ปรับ (+ เพิ่ม / - ลด)`,
      inputValue: isDispose ? String(Number(lot.ml_qty_left)) : '',
      showCancelButton: true,
      confirmButtonText: 'ถัดไป',
      cancelButtonText: 'ปิด',
      reverseButtons: true,
    });
    if (!value) return;

    const { value: reason } = await Swal.fire({
      title: 'เหตุผล',
      input: 'text',
      inputPlaceholder: isDispose ? 'เช่น หมดอายุ / ปนเปื้อน' : 'เช่น นับสต๊อกแล้วไม่ตรง',
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ปิด',
      reverseButtons: true,
    });
    if (!reason) return;

    axiosInstance.post(`/api/nurse/medicines/lots/${lot.ml_id}/${kind}`, { amount: value, reason })
      .then(() => {
        refreshAll();
        Swal.fire({ icon: 'success', title: 'บันทึกแล้ว', timer: 1200, showConfirmButton: false });
      })
      .catch(err => alertError('ไม่สามารถบันทึกได้', err));
  };

  const summary = alerts.summary || {};

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-3">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">ห้องพยาบาล</li>
              <li className="breadcrumb-item active">คลังยาและเวชภัณฑ์</li>
            </ol>
            <h4 className="main-title mb-0">คลังยาและเวชภัณฑ์</h4>
            <div className="fs-xs text-secondary">
              จ่ายยาแบบล็อตที่หมดอายุก่อนจ่ายก่อน · ยาที่หมดอายุแล้วระบบไม่จ่ายให้
            </div>
          </div>
          {canManage && (
            <Button className="mt-3 mt-md-0" onClick={() => openItem(null)}>
              <i className="ri-add-line me-1"></i>เพิ่มรายการยา
            </Button>
          )}
        </div>

        <Row className="g-3 mb-3">
          <Col xs={6} lg={3}>
            <Card className="card-one"><Card.Body className="p-3">
              <div className="fs-xs text-secondary">รายการยาที่ใช้งาน</div>
              <h3 className="card-value mb-0">{summary.item_count ?? 0}</h3>
            </Card.Body></Card>
          </Col>
          <Col xs={6} lg={3}>
            <Card className="card-one"><Card.Body className="p-3">
              <div className="fs-xs text-secondary">ต่ำกว่าจำนวนขั้นต่ำ</div>
              <h3 className={`card-value mb-0 ${summary.low_stock ? 'text-warning' : ''}`}>{summary.low_stock ?? 0}</h3>
            </Card.Body></Card>
          </Col>
          <Col xs={6} lg={3}>
            <Card className="card-one"><Card.Body className="p-3">
              <div className="fs-xs text-secondary">ใกล้หมดอายุ (60 วัน)</div>
              <h3 className={`card-value mb-0 ${summary.expiring_soon ? 'text-warning' : ''}`}>{summary.expiring_soon ?? 0}</h3>
            </Card.Body></Card>
          </Col>
          <Col xs={6} lg={3}>
            <Card className="card-one"><Card.Body className="p-3">
              <div className="fs-xs text-secondary">หมดอายุแล้ว (รอทิ้ง)</div>
              <h3 className={`card-value mb-0 ${summary.expired ? 'text-danger' : ''}`}>{summary.expired ?? 0}</h3>
            </Card.Body></Card>
          </Col>
        </Row>

        <Nav variant="tabs" activeKey={tab} onSelect={setTab} className="mb-3">
          <Nav.Item><Nav.Link eventKey="stock">คลัง</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="alerts">ใกล้หมดอายุ / ใกล้หมด</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="txns">ประวัติการเคลื่อนไหว</Nav.Link></Nav.Item>
        </Nav>

        {tab === 'stock' && (
          <Row className="g-3">
            <Col xl={6}>
              <Card className="card-one">
                <Card.Header className="d-flex align-items-center gap-2">
                  <Card.Title as="h6" className="mb-0">รายการยา</Card.Title>
                  <Form.Check type="switch" id="show-inactive" className="ms-auto fs-xs"
                    label="รวมที่ปิดใช้งาน" checked={showInactive}
                    onChange={e => setShowInactive(e.target.checked)} />
                </Card.Header>
                <Card.Body className="pb-0">
                  <InputGroup size="sm" className="mb-2">
                    <Form.Control value={keyword} onChange={e => setKeyword(e.target.value)}
                      placeholder="ค้นชื่อยา ชื่อสามัญ หรือรหัส" />
                    {keyword && (
                      <Button variant="outline-secondary" onClick={() => setKeyword('')} aria-label="ล้างคำค้นหา">
                        <i className="ri-close-line"></i>
                      </Button>
                    )}
                  </InputGroup>
                </Card.Body>
                <Card.Body className="p-0">
                  <div className="table-responsive" style={{ maxHeight: 520, overflowY: 'auto' }}>
                    <Table hover className="mb-0 fs-sm">
                      <thead><tr><th>ยา</th><th className="text-end">คงเหลือ</th><th>หมดอายุถัดไป</th></tr></thead>
                      <tbody>
                        {items.length === 0 ? (
                          <tr><td colSpan={3} className="text-center text-secondary py-4">ยังไม่มีรายการยา</td></tr>
                        ) : items.map(item => (
                          <tr key={item.mi_id} role="button"
                            className={selected?.mi_id === item.mi_id ? 'table-active' : ''}
                            onClick={() => setSelected(item)}>
                            <td>
                              <div className="fw-semibold">
                                {item.mi_name}
                                {item.mi_is_controlled ? <Badge bg="dark" className="ms-1 fw-normal">ควบคุม</Badge> : null}
                                {item.mi_status === 'inactive' ? <Badge bg="secondary" className="ms-1 fw-normal">ปิดใช้งาน</Badge> : null}
                              </div>
                              <div className="fs-xs text-secondary">
                                {item.mi_code} · {formLabel(item.mi_form)}
                                {item.mi_generic ? ` · ${item.mi_generic}` : ''}
                              </div>
                            </td>
                            <td className="text-end align-middle">
                              <Badge bg={stockVariant(item)}>{Number(item.stock)} {item.mi_unit}</Badge>
                              {Number(item.mi_min_stock) > 0 && (
                                <div className="fs-xs text-secondary">ขั้นต่ำ {Number(item.mi_min_stock)}</div>
                              )}
                              {Number(item.expired_stock) > 0 && (
                                <div className="fs-xs text-danger">หมดอายุ {Number(item.expired_stock)}</div>
                              )}
                            </td>
                            <td className="align-middle">
                              {item.next_expiry
                                ? <Badge bg={expiryVariant(item.next_expiry)}>{thaiDate(item.next_expiry)}</Badge>
                                : <span className="text-secondary fs-xs">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={6}>
              {!selected ? (
                <Card className="card-one"><Card.Body className="text-center text-secondary py-5">
                  เลือกรายการยาทางซ้ายเพื่อดูล็อตและรับยาเข้าคลัง
                </Card.Body></Card>
              ) : (
                <Card className="card-one">
                  <Card.Header className="d-flex align-items-center gap-2">
                    <div>
                      <Card.Title as="h6" className="mb-0">{selected.mi_name}</Card.Title>
                      <div className="fs-xs text-secondary">{selected.mi_code} · หน่วย {selected.mi_unit}</div>
                    </div>
                    {canManage && (
                      <div className="ms-auto d-flex gap-2">
                        <Button size="sm" variant="outline-secondary" onClick={() => openItem(selected)}>
                          <i className="ri-edit-line"></i>
                        </Button>
                        <Button size="sm" onClick={() => { setLotForm(emptyLot()); setShowLotModal(true); }}>
                          <i className="ri-add-line me-1"></i>รับเข้าคลัง
                        </Button>
                      </div>
                    )}
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table className="mb-0 fs-sm">
                      <thead>
                        <tr>
                          <th>ล็อต</th><th>หมดอายุ</th>
                          <th className="text-end">คงเหลือ</th><th>รับเข้า</th>
                          {canManage && <th style={{ width: 90 }}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {lots.length === 0 ? (
                          <tr><td colSpan={canManage ? 5 : 4} className="text-center text-secondary py-4">
                            ยังไม่มีล็อต — กด “รับเข้าคลัง” เพื่อเพิ่ม
                          </td></tr>
                        ) : lots.map(lot => (
                          <tr key={lot.ml_id} className={Number(lot.ml_qty_left) === 0 ? 'text-secondary' : ''}>
                            <td>
                              {lot.ml_lot_no || <span className="text-secondary">ไม่ระบุ</span>}
                              <div className="fs-xs text-secondary">{medSourceLabel(lot.ml_source)}</div>
                            </td>
                            <td>
                              {lot.ml_expiry_date
                                ? <Badge bg={expiryVariant(lot.ml_expiry_date)}>{thaiDate(lot.ml_expiry_date)}</Badge>
                                : <span className="fs-xs text-secondary">ไม่มีวันหมดอายุ</span>}
                            </td>
                            <td className="text-end">
                              {Number(lot.ml_qty_left)} / {Number(lot.ml_qty_received)}
                            </td>
                            <td className="fs-xs text-secondary">
                              {thaiDate(lot.ml_received_at)}
                              <div>{lot.received_by_name}</div>
                            </td>
                            {canManage && (
                              <td className="text-end text-nowrap">
                                <Button size="sm" variant="outline-secondary" className="me-1"
                                  onClick={() => stockAction(lot, 'adjust')} aria-label="ปรับยอด">
                                  <i className="ri-scales-3-line"></i>
                                </Button>
                                <Button size="sm" variant="outline-danger"
                                  disabled={Number(lot.ml_qty_left) <= 0}
                                  onClick={() => stockAction(lot, 'dispose')} aria-label="ทิ้งยา">
                                  <i className="ri-delete-bin-line"></i>
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        )}

        {tab === 'alerts' && (
          <Row className="g-3">
            <Col xl={6}>
              <Card className="card-one">
                <Card.Header><Card.Title as="h6" className="mb-0">ใกล้หมดอายุ / หมดอายุแล้ว</Card.Title></Card.Header>
                <Card.Body className="p-0">
                  {!alerts.expiring.length ? (
                    <div className="text-center text-secondary py-4 fs-sm">ไม่มีล็อตที่ใกล้หมดอายุ</div>
                  ) : (
                    <Table className="mb-0 fs-sm">
                      <thead><tr><th>ยา</th><th>ล็อต</th><th>หมดอายุ</th><th className="text-end">คงเหลือ</th></tr></thead>
                      <tbody>
                        {alerts.expiring.map(l => (
                          <tr key={l.ml_id}>
                            <td>{l.mi_name}</td>
                            <td className="fs-xs">{l.ml_lot_no || '—'}</td>
                            <td>
                              <Badge bg={Number(l.days_left) < 0 ? 'danger' : 'warning'}>
                                {thaiDate(l.ml_expiry_date)}
                              </Badge>
                              <div className="fs-xs text-secondary">
                                {Number(l.days_left) < 0 ? `เกินมา ${-l.days_left} วัน` : `อีก ${l.days_left} วัน`}
                              </div>
                            </td>
                            <td className="text-end">{Number(l.ml_qty_left)} {l.mi_unit}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col xl={6}>
              <Card className="card-one">
                <Card.Header><Card.Title as="h6" className="mb-0">ต่ำกว่าจำนวนขั้นต่ำ</Card.Title></Card.Header>
                <Card.Body className="p-0">
                  {!alerts.low.length ? (
                    <div className="text-center text-secondary py-4 fs-sm">ทุกรายการอยู่เหนือจำนวนขั้นต่ำ</div>
                  ) : (
                    <Table className="mb-0 fs-sm">
                      <thead><tr><th>ยา</th><th className="text-end">คงเหลือ</th><th className="text-end">ขั้นต่ำ</th></tr></thead>
                      <tbody>
                        {alerts.low.map(l => (
                          <tr key={l.mi_id}>
                            <td>{l.mi_name}</td>
                            <td className="text-end text-warning fw-semibold">{Number(l.stock)} {l.mi_unit}</td>
                            <td className="text-end text-secondary">{Number(l.mi_min_stock)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {tab === 'txns' && (
          <Card className="card-one">
            <Card.Header><Card.Title as="h6" className="mb-0">ประวัติการเคลื่อนไหว 200 รายการล่าสุด</Card.Title></Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 fs-sm">
                  <thead>
                    <tr><th>เวลา</th><th>ยา</th><th>ประเภท</th><th className="text-end">จำนวน</th><th>เหตุผล / ผู้รับ</th><th>ผู้ทำ</th></tr>
                  </thead>
                  <tbody>
                    {txns.length === 0 ? (
                      <tr><td colSpan={6} className="text-center text-secondary py-4">ยังไม่มีการเคลื่อนไหว</td></tr>
                    ) : txns.map(t => (
                      <tr key={t.mt_id}>
                        <td className="text-nowrap fs-xs">{t.mt_at}</td>
                        <td>
                          {t.mi_name}
                          <div className="fs-xs text-secondary">
                            {t.ml_lot_no ? `ล็อต ${t.ml_lot_no}` : 'ไม่ระบุล็อต'}
                            {t.ml_expiry_date ? ` · หมดอายุ ${thaiDate(t.ml_expiry_date)}` : ''}
                          </div>
                        </td>
                        <td>
                          <Badge bg={{ dispense: 'primary', adjust: 'secondary', dispose: 'danger', return: 'info' }[t.mt_type]}>
                            {{ dispense: 'จ่ายยา', adjust: 'ปรับยอด', dispose: 'ทิ้ง', return: 'คืน' }[t.mt_type]}
                          </Badge>
                        </td>
                        <td className={`text-end fw-semibold ${Number(t.mt_qty) < 0 ? 'text-danger' : 'text-success'}`}>
                          {Number(t.mt_qty) > 0 ? '+' : ''}{Number(t.mt_qty)} {t.mi_unit}
                        </td>
                        <td className="fs-xs">
                          {t.st_name ? <div>ให้ {t.st_name}</div> : null}
                          {t.mt_reason}
                        </td>
                        <td className="fs-xs text-secondary">{t.by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}

        <Footer />
      </div>

      {/* รายการยา */}
      <Modal show={showItemModal} onHide={() => setShowItemModal(false)} centered>
        <Form onSubmit={saveItem}>
          <Modal.Header closeButton>
            <Modal.Title className="fs-16">{itemForm.mi_id ? 'แก้ไขรายการยา' : 'เพิ่มรายการยา'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-2">
              <Col xs={5}>
                <Form.Label className="fw-semibold fs-xs mb-1">รหัส</Form.Label>
                <Form.Control size="sm" value={itemForm.code} required maxLength={50}
                  onChange={e => setItemForm({ ...itemForm, code: e.target.value })} />
              </Col>
              <Col xs={7}>
                <Form.Label className="fw-semibold fs-xs mb-1">ชื่อยา</Form.Label>
                <Form.Control size="sm" value={itemForm.name} required maxLength={255}
                  onChange={e => setItemForm({ ...itemForm, name: e.target.value })} />
              </Col>
              <Col xs={12}>
                <Form.Label className="fw-semibold fs-xs mb-1">ชื่อสามัญทางยา</Form.Label>
                <Form.Control size="sm" value={itemForm.generic}
                  onChange={e => setItemForm({ ...itemForm, generic: e.target.value })} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fw-semibold fs-xs mb-1">รูปแบบ</Form.Label>
                <Form.Select size="sm" value={itemForm.form}
                  onChange={e => setItemForm({ ...itemForm, form: e.target.value })}>
                  {MED_FORMS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </Form.Select>
              </Col>
              <Col xs={6}>
                <Form.Label className="fw-semibold fs-xs mb-1">หน่วยนับ</Form.Label>
                <Form.Control size="sm" value={itemForm.unit} required
                  onChange={e => setItemForm({ ...itemForm, unit: e.target.value })} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fw-semibold fs-xs mb-1">หมวด</Form.Label>
                <Form.Control size="sm" value={itemForm.category}
                  onChange={e => setItemForm({ ...itemForm, category: e.target.value })} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fw-semibold fs-xs mb-1">จำนวนขั้นต่ำ</Form.Label>
                <Form.Control size="sm" type="number" min="0" step="0.01" value={itemForm.minStock}
                  onChange={e => setItemForm({ ...itemForm, minStock: e.target.value })} />
              </Col>
              <Col xs={12}>
                <Form.Check type="switch" id="med-controlled" label="ยาควบคุมพิเศษ"
                  checked={itemForm.isControlled}
                  onChange={e => setItemForm({ ...itemForm, isControlled: e.target.checked })} />
                <Form.Check type="switch" id="med-active" label="ใช้งาน"
                  checked={itemForm.status === 'active'}
                  onChange={e => setItemForm({ ...itemForm, status: e.target.checked ? 'active' : 'inactive' })} />
              </Col>
              <Col xs={12}>
                <Form.Label className="fw-semibold fs-xs mb-1">หมายเหตุ</Form.Label>
                <Form.Control size="sm" as="textarea" rows={2} value={itemForm.note}
                  onChange={e => setItemForm({ ...itemForm, note: e.target.value })} />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowItemModal(false)}>ปิด</Button>
            <Button type="submit" disabled={saving}>บันทึก</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* รับเข้าคลัง */}
      <Modal show={showLotModal} onHide={() => setShowLotModal(false)} centered>
        <Form onSubmit={saveLot}>
          <Modal.Header closeButton>
            <Modal.Title className="fs-16">รับเข้าคลัง — {selected?.mi_name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-2">
              <Col xs={6}>
                <Form.Label className="fw-semibold fs-xs mb-1">เลขล็อต</Form.Label>
                <Form.Control size="sm" value={lotForm.lotNo} maxLength={50}
                  onChange={e => setLotForm({ ...lotForm, lotNo: e.target.value })} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fw-semibold fs-xs mb-1">วันหมดอายุ</Form.Label>
                <Form.Control size="sm" type="date" value={lotForm.expiryDate} min={todayISO()}
                  onChange={e => setLotForm({ ...lotForm, expiryDate: e.target.value })} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fw-semibold fs-xs mb-1">จำนวน ({selected?.mi_unit})</Form.Label>
                <Form.Control size="sm" type="number" min="0.01" step="0.01" value={lotForm.qty} required
                  onChange={e => setLotForm({ ...lotForm, qty: e.target.value })} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fw-semibold fs-xs mb-1">ราคาต่อหน่วย</Form.Label>
                <Form.Control size="sm" type="number" min="0" step="0.01" value={lotForm.unitCost}
                  onChange={e => setLotForm({ ...lotForm, unitCost: e.target.value })} />
              </Col>
              <Col xs={12}>
                <Form.Label className="fw-semibold fs-xs mb-1">ที่มา</Form.Label>
                <Form.Select size="sm" value={lotForm.source}
                  onChange={e => setLotForm({ ...lotForm, source: e.target.value })}>
                  {MED_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Form.Select>
              </Col>
              <Col xs={12}>
                <Form.Label className="fw-semibold fs-xs mb-1">หมายเหตุ</Form.Label>
                <Form.Control size="sm" value={lotForm.note} maxLength={255}
                  onChange={e => setLotForm({ ...lotForm, note: e.target.value })} />
              </Col>
              <Col xs={12}>
                <div className="fs-xs text-secondary">
                  ล็อตที่ไม่ใส่วันหมดอายุจะถูกจ่ายหลังสุด — ระบบจ่ายล็อตที่หมดอายุก่อนเป็นอันดับแรก
                </div>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowLotModal(false)}>ปิด</Button>
            <Button type="submit" disabled={saving}>รับเข้าคลัง</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}
